// src/public-routes/sectors/AllSectorsPage.jsx
import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaGlobe,
    FaLightbulb,
    FaCogs,
    FaChartLine,
    FaUsers,
    FaHandshake,
    FaRocket,
    FaTrophy
} from 'react-icons/fa';

const AllSectorsPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const stats = [
        { number: '94%', label: 'of businesses see improved ROI with tailored digital strategies' },
        { number: '3.5x', label: 'higher conversion rates with sector-specific marketing' },
        { number: '78%', label: 'of companies prioritize digital transformation' },
        { number: '89%', label: 'client retention rate across all sectors' }
    ];

    const approaches = [
        {
            title: "Tailored Strategy Development",
            description: "Custom-built digital marketing strategies designed specifically for your industry, audience, and unique business goals.",
            icon: FaLightbulb
        },
        {
            title: "Integrated Campaign Management",
            description: "Seamless multi-channel campaigns that work together across search, social, display, and content to maximize reach and ROI.",
            icon: FaCogs
        },
        {
            title: "Performance Optimization",
            description: "Continuous testing, analysis, and refinement to ensure your campaigns deliver measurable results and improving returns.",
            icon: FaChartLine
        },
        {
            title: "Growth Partnership",
            description: "Long-term strategic partnership focused on sustainable growth, adapting to market changes and scaling with your success.",
            icon: FaHandshake
        }
    ];

    const benefits = [
        ['Customized Strategies', 'Solutions built around your specific business model, not generic templates'],
        ['Measurable Results', 'Clear KPIs and transparent reporting that demonstrate real business impact'],
        ['Scalable Growth', 'Strategies designed to grow with your business and adapt to market changes'],
        ['Competitive Advantage', 'Differentiate your brand with unique positioning and market-specific insights'],
        ['Long-Term Partnership', 'Ongoing optimization and support as your business evolves']
    ];

    const questions = [
        'How can digital marketing transform your specific business?',
        'What strategies will work best for your unique market?',
        'How do you stand out in your industry?',
        'Which channels will deliver the best ROI for your business?',
        'How can you future-proof your digital presence?'
    ];

    return (
        <div className="all-sectors-page st-page">
            {/* Top Image Section */}
            <div className="st-decorative-image-container">
                <img
                    src={require('./ALL.PNG')}
                    alt="Digital Marketing Solutions for All Business Sectors"
                    loading="eager"
                />
            </div>

            {/* Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">All Sectors</span> Digital Marketing
                    </h1>
                    <p>Every business is unique — and so is our approach. Tailored digital marketing strategies for businesses across all industries</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    While we bring strong experience across specific industries, our expertise is <span className="sv-keyword-highlight">not limited to them</span>. Every business is unique — and so is our approach to digital marketing.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>digital marketing services</strong> are designed to adapt to your specific business model, audience, and growth goals. We don't believe in one-size-fits-all solutions. Instead, we take the time to understand your market dynamics and create strategies that deliver real results.
                                </p>
                                <p className="sv-description">
                                    Whether you operate in a niche industry, emerging market, or established sector, our team brings the strategic thinking, data intelligence, and creative expertise needed to drive your business forward in the digital age.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Your Strategy
                                    </Button>
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Digital Marketing</span> Matters for Every Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {stats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What We Do Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Do for Your Business</h2>
                                <p className="sv-section-description">
                                    <strong>Digital marketing</strong> is not just about being online — it's about creating meaningful connections with your audience, driving measurable growth, and building a sustainable competitive advantage in your market.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions We Answer:</h4>
                                    {questions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Overview */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in Digital Marketing?</h2>
                                <p className="sv-section-description">
                                    In today's digital-first world, every business needs a strong online presence. A comprehensive digital marketing strategy helps you reach the right audience, build meaningful relationships, and drive sustainable growth regardless of your industry.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-globe"></i>
                                        <h5>Broader Reach</h5>
                                        <p>Connect with your ideal customers wherever they are online</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Targeted Approach</h5>
                                        <p>Reach the right people with the right message at the right time</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-bar"></i>
                                        <h5>Measurable Results</h5>
                                        <p>Track every dollar and optimize for maximum ROI</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-sync"></i>
                                        <h5>Continuous Improvement</h5>
                                        <p>Adapt and refine strategies based on real performance data</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Digital Marketing Services</h2>
                                <p className="sv-section-description">
                                    Reachtix offers a comprehensive suite of digital marketing services tailored to your unique business needs, market position, and growth objectives.
                                </p>

                                <Row className="sv-services-grid">
                                    {approaches.map((service, index) => (
                                        <Col lg={6} md={6} key={index} className="mb-4">
                                            <div className="sv-approach-card p-4 h-100">
                                                <div className="sv-approach-icon">
                                                    <service.icon />
                                                </div>
                                                <div className="sv-approach-content">
                                                    <h4>{service.title}</h4>
                                                    <p className="service-description">{service.description}</p>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Table Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of Digital Marketing With Reachtix</h2>
                                <div className="table-responsive">
                                    <table className="table sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Main CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Transform Your Business?</h2>
                                <p className="sv-cta-description">
                                    Whatever your industry, whatever your goals, our team has the expertise and creativity to develop a digital marketing strategy that drives real results for your business.
                                </p>
                                <p className="sv-cta-description">
                                    If you operate in a different sector, we'd love to hear from you. Let's develop a tailored strategy aligned with your business model, audience, and growth goals.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="primary">
                                        <i className="fas fa-calendar-check mr-2"></i>
                                        Start Your Consultation
                                    </Button>
                                    <Button tag={Link} to="/contact" color="light">
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get Business Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AllSectorsPage;